import { Directive, Host, OnDestroy, OnInit } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

/**
 * Closes ng-select when the user scrolls (mouse wheel, scrollbar, touchpad)
 * or uses keyboard keys that scroll the page/container, so the panel never
 * drifts or clips while the viewport moves.
 */
@Directive({
  selector: 'ng-select',
  standalone: false,
})
export class NgSelectScrollFixDirective implements OnInit, OnDestroy {
  private scrollHandler!: EventListener;
  private wheelHandler!: EventListener;
  private keydownHandler!: EventListener;
  private scrollableContainer: HTMLElement | null = null;
  private lastScrollLeft = 0;

  constructor(@Host() private ngSelect: NgSelectComponent) {}

  ngOnInit() {
    this.scrollableContainer = this.findScrollableParent(this.ngSelect.element);
    this.lastScrollLeft = this.scrollableContainer?.scrollLeft ?? 0;

    this.scrollHandler = (event: Event) => {
      if (this.isEventFromDropdownPanel(event)) {
        return;
      }

      // Close only on actual horizontal movement.
      if (this.scrollableContainer) {
        const currentLeft = this.scrollableContainer.scrollLeft;
        if (currentLeft !== this.lastScrollLeft) {
          this.lastScrollLeft = currentLeft;
          this.closeIfOpen();
        }
      }
    };
    this.wheelHandler = (event: Event) => {
      if (this.isEventFromDropdownPanel(event)) {
        return;
      }
      if (Math.abs((event as WheelEvent).deltaX) > 0) {
        this.closeIfOpen();
      }
    };
    this.keydownHandler = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      const horizontalScrollKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!horizontalScrollKeys.includes(keyboardEvent.key)) {
        return;
      }

      this.closeIfOpen();
    };

    this.scrollableContainer?.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('wheel', this.wheelHandler, { capture: true, passive: true });
    window.addEventListener('keydown', this.keydownHandler, true);
  }

  ngOnDestroy() {
    this.scrollableContainer?.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('wheel', this.wheelHandler, { capture: true } as AddEventListenerOptions);
    window.removeEventListener('keydown', this.keydownHandler, true);
  }

  /** `appendTo="body"` renders the panel outside the host; ignore its own scroll/wheel. */
  private isEventFromDropdownPanel(event: Event): boolean {
    const t = event.target;
    if (!(t instanceof Element)) {
      return false;
    }
    return !!t.closest('.ng-dropdown-panel');
  }

  private closeIfOpen() {
    if (this.ngSelect?.isOpen()) {
      this.ngSelect.close();
    }
  }

  private findScrollableParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const ox = style.overflowX;
      const oy = style.overflowY;
      if (
        ox === 'auto' ||
        ox === 'scroll' ||
        oy === 'auto' ||
        oy === 'scroll'
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }
}
